import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight, EnvelopeSimple } from "@phosphor-icons/react";
import { impactAchievements, people } from "../../data/content.js";
import { AAROH_IMAGES } from "../../data/images.js";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import "./learn.css";

const SUBMIT_MAIL = "mailto:founder@projectgrid.org?subject=Student%20project%20account";

const chillgui = impactAchievements.find((item) => item.slug === "chillgui-overall-winner");
const aurora = impactAchievements.find((item) => item.slug === "aurora-global-hackathon-2026");
const metamorphosis = impactAchievements.find((item) => item.slug === "metamorphosis-outreach");
const jerovin = people.find((person) => person.slug === "jerovin-floyd-vincent-joseph");
const gantavya = people.find((person) => person.slug === "gantavya-rohilla");

const SEND = [
  "The project name and every team member",
  "The problem you investigated, and who has it",
  "What you built",
  "What you learned from testing it",
  "A demo, or photos you have permission to publish",
];

// Moves a column against the scroll so the gallery reads as two layers.
function Drift({ children, amount = 80, className = "" }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, (value) => (0.5 - value) * amount);
  return (
    <motion.div ref={ref} className={className} style={reduceMotion || !wide ? undefined : { y }}>
      {children}
    </motion.div>
  );
}

function Feature() {
  return (
    <article className="lb-pj lb-pj--feature" aria-labelledby="lb-pj-chillgui">
      <ClipPhoto
        className="lb-pj__photo lb-pj__photo--tall"
        src="/images/evidence/jerovin-chillgui-02.jpg"
        alt={chillgui.imageAlt}
        caption="Jerovin with the laptop he won as Aurora's overall winner."
        sizes="(max-width: 900px) 100vw, 42vw"
      />
      <div className="lb-pj__body">
        <span className="lb-mono">Aurora 2026 · Overall winner · Solo builder</span>
        <h2 id="lb-pj-chillgui">ChillGui</h2>
        <p className="lb-pj__by">by {jerovin.name}, a student at IIIT Delhi</p>
        <p>{jerovin.story}</p>
        <p>ChillGui is an Edge-AI layer that simplifies everyday app interfaces for older users.</p>
        <ul className="lb-pj__tags" aria-label="Project details">
          {jerovin.highlights.map((tag) => <li key={tag}>{tag}</li>)}
        </ul>
        <div className="lb-pj__evidence">
          <figure>
            <div className="lb-pj__evimg"><Photo src="/images/evidence/aurora-winner-01.jpg" alt="The ChillGui project mark: app simplification middleware" sizes="200px" /></div>
            <figcaption>The ChillGui project mark.</figcaption>
          </figure>
          <figure>
            <div className="lb-pj__evimg"><Photo src="/images/evidence/aurora-winner-02.jpg" alt="Aurora overall winner certificate for Jerovin Floyd" sizes="200px" /></div>
            <figcaption>The overall-winner certificate.</figcaption>
          </figure>
        </div>
        <a className="gh-link" href={chillgui.sourceUrl} target="_blank" rel="noreferrer">
          {chillgui.sourceLabel} <ArrowUpRight weight="bold" aria-hidden="true" />
        </a>
      </div>
    </article>
  );
}

function Second() {
  return (
    <article className="lb-pj lb-pj--second" aria-labelledby="lb-pj-gantavya">
      <div className="lb-pj__body">
        <span className="lb-mono">Aurora 2026 · Best Innovation</span>
        <h2 id="lb-pj-gantavya">A handwriting model that learns your style</h2>
        <p className="lb-pj__by">by {gantavya.name}, a third-year student at IIT Madras</p>
        <p>A personalised handwriting system designed to move note-taking into physical space.</p>
        <p>{gantavya.story}</p>
        <ul className="lb-pj__tags" aria-label="Project details">
          {gantavya.highlights.map((tag) => <li key={tag}>{tag}</li>)}
        </ul>
        <a className="gh-link" href={gantavya.sourceUrl} target="_blank" rel="noreferrer">
          {gantavya.sourceLabel} <ArrowUpRight weight="bold" aria-hidden="true" />
        </a>
      </div>
      <Drift className="lb-pj__drift" amount={120}>
        <ClipPhoto
          className="lb-pj__photo lb-pj__photo--tall"
          src="/images/evidence/gantavya-project.jpg"
          alt="Gantavya Rohilla holding his Aurora prize laptop"
          caption="Gantavya with his Aurora prize laptop."
          sizes="(max-width: 900px) 100vw, 34vw"
        />
      </Drift>
    </article>
  );
}

function ProgrammeRecords() {
  return (
    <section className="lb-records" aria-labelledby="lb-records-title">
      <header className="lb-records__head">
        <MaskWords id="lb-records-title" text="Where the work came from." />
        <p>The programmes these projects came from, and what each one produced.</p>
      </header>
      <div className="lb-records__grid">
        <article className="lb-rec lb-rec--aurora">
          <div className="lb-rec__banner">
            <Photo src={aurora.image} alt={aurora.imageAlt} sizes="(max-width: 900px) 100vw, 60vw" />
          </div>
          <div className="lb-rec__body">
            <span className="lb-mono">{aurora.eyebrow}</span>
            <h3>{aurora.title}</h3>
            <p>{aurora.summary}</p>
            <ul className="lb-rec__facts">{aurora.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>
            <a className="gh-link" href={aurora.sourceUrl} target="_blank" rel="noreferrer">
              {aurora.sourceLabel} <ArrowUpRight weight="bold" aria-hidden="true" />
            </a>
          </div>
        </article>
        <Drift className="lb-rec lb-rec--aaroh" amount={90}>
          <ClipPhoto src={AAROH_IMAGES.recognition} alt="Students receiving recognition on stage at AAROH, IIIT Delhi" sizes="(max-width: 900px) 100vw, 36vw" />
          <span className="lb-mono">Completed · GRID organised</span>
          <h3>AAROH, IIIT Delhi</h3>
          <p>363 young innovators built across AI/ML, game development, cybersecurity and hardware over two days. Thirteen teams were recognised.</p>
          <Link className="gh-link" to="/programmes/aaroh">Read the AAROH recap <ArrowRight weight="bold" aria-hidden="true" /></Link>
        </Drift>
        <article className="lb-rec lb-rec--meta">
          <ClipPhoto src="/images/grid-photo-08-p7.jpg" alt="Students visiting a makerspace at IIT Delhi during Metamorphosis" sizes="(max-width: 900px) 100vw, 40vw" />
          <span className="lb-mono">{metamorphosis.eyebrow}</span>
          <h3>{metamorphosis.title}</h3>
          <p>{metamorphosis.summary}</p>
          <ul className="lb-rec__facts">{metamorphosis.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>
          <Link className="gh-link" to="/programmes/metamorphosis">How GRID supported it <ArrowRight weight="bold" aria-hidden="true" /></Link>
        </article>
      </div>
    </section>
  );
}

export default function ProjectsPage() {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <PageFrame className="rd-page learn-page lb-projects">
      <Seo
        title="Student Projects"
        path="/projects"
        description="Student-led projects and prototypes from Project GRID programmes, including Aurora's overall winner and Best Innovation award."
      />
      <main>
        <header className="lb-pjhero">
          <motion.p className="lb-mono" {...appear(0)}>Student projects · Aurora 2026</motion.p>
          <MaskWords as="h1" text="Show the work, not only the idea." onMount delay={0.05} />
          <div className="lb-pjhero__row">
            <motion.p {...appear(0.4)}>
              Projects students built in GRID programmes. Each one names the builder, the problem and what they made.
            </motion.p>
            <motion.div className="gh-actions" {...appear(0.5)}>
              <a className="gh-btn gh-btn--fill" href={SUBMIT_MAIL}>
                <EnvelopeSimple weight="bold" aria-hidden="true" />
                <span className="roll" data-label="Share your project"><span>Share your project</span></span>
              </a>
              <Link className="gh-btn gh-btn--ghost" to="/programmes/aurora">
                <span className="roll" data-label="Aurora results"><span>Aurora results</span></span>
              </Link>
            </motion.div>
          </div>
        </header>

        <section className="lb-gallery" aria-label="Published projects">
          <Feature />
          <Second />
        </section>

        <ProgrammeRecords />

        <section className="lb-growing" aria-labelledby="lb-growing-title">
          <div className="lb-growing__note">
            <span className="lb-chip">More to come</span>
            <h2 id="lb-growing-title">More projects are on the way.</h2>
            <p>
              Thirteen AAROH teams were recognised and Aurora's Top 20 entered incubation. We publish each project with the team's permission and enough detail to explain what was built and tested.
            </p>
          </div>
          <div className="lb-submit">
            <MaskWords id="lb-submit-title" text="Share your project with GRID." />
            <p>Built something at a GRID programme, or with our guides? Email us and include:</p>
            <ol className="lb-submit__list">
              {SEND.map((item, index) => (
                <li key={item}><span aria-hidden="true">{index + 1}</span>{item}</li>
              ))}
            </ol>
            <div className="gh-actions">
              <a className="gh-btn gh-btn--fill" href={SUBMIT_MAIL}>
                <EnvelopeSimple weight="bold" aria-hidden="true" />
                <span className="roll" data-label="Email founder@projectgrid.org"><span>Email founder@projectgrid.org</span></span>
              </a>
              <Link className="gh-link" to="/guides/pitch-a-student-project">Write it up like a pitch <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </div>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
